// logout.component.ts
import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { OrderFlowService } from '../../order-flow/order-flow.service';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/auth/auth.service';
import { StorageService } from '../../../core/storage.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-login-successfully',
  templateUrl: 'login-successfully.component.html',
  standalone: true,
  imports: [CommonModule],
})
export class LoginSucessfullyComponent implements OnInit, OnDestroy {

  // Usar apenas um estado para controlar tudo - mais claro e sem ambiguidade
  currentState: 'loading' | 'success' | 'error' = 'loading';
  errorMessage = '';
  
  // ID único para rastrear instâncias
  componentId = Math.random().toString(36).substr(2, 9);
  
  // Prevenir múltiplas execuções
  private validationStarted = false;
  private subscription?: Subscription;
  
  // Control global para prevenir múltiplas instâncias
  private static activeInstance: string | null = null;
  isActiveInstance = false;

  constructor(
    private router: Router, 
    private storageService: StorageService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    console.log(`[${this.componentId}] Constructor - currentState:`, this.currentState);
    
    // Se já existe uma instância ativa, marcar esta como inativa
    if (LoginSucessfullyComponent.activeInstance) {
      console.log(`[${this.componentId}] Another instance (${LoginSucessfullyComponent.activeInstance}) is active, marking this as inactive`);
      this.isActiveInstance = false;
      this.currentState = 'loading'; // Estado neutro para instâncias inativas
    } else {
      // Esta é a primeira/única instância
      LoginSucessfullyComponent.activeInstance = this.componentId;
      this.isActiveInstance = true;
      console.log(`[${this.componentId}] This is now the active instance`);
    }
  }

  ngOnInit(): void {
    console.log(`[${this.componentId}] ngOnInit - currentState:`, this.currentState, 'isActive:', this.isActiveInstance);
    
    // Só executar se for a instância ativa
    if (this.isActiveInstance) {
      this.performValidation();
    } else {
      console.log(`[${this.componentId}] Skipping validation - not active instance`);
      // Instâncias inativas ficam em loading permanentemente (invisíveis)
    }
  }

  ngOnDestroy(): void {
    console.log(`[${this.componentId}] ngOnDestroy - cleaning up`);
    
    // Limpar subscription se existir
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    
    // Se esta era a instância ativa, limpar a referência global
    if (this.isActiveInstance && LoginSucessfullyComponent.activeInstance === this.componentId) {
      LoginSucessfullyComponent.activeInstance = null;
      console.log(`[${this.componentId}] Cleared active instance reference`);
    }
  }

  /**
   * Performs user session validation.
   */
  private performValidation(): void {
    // Verificar se ainda é a instância ativa
    if (!this.isActiveInstance) {
      console.log(`[${this.componentId}] Not active instance, aborting validation`);
      return;
    }
    
    // Prevenir múltiplas execuções
    if (this.validationStarted) {
      console.log(`[${this.componentId}] performValidation already started, skipping...`);
      return;
    }
    
    this.validationStarted = true;
    
    // Garantir estado inicial
    this.currentState = 'loading';
    this.errorMessage = '';
    this.cdr.detectChanges();
    console.log(`[${this.componentId}] performValidation started - currentState:`, this.currentState);

    setTimeout(() => {
      // Verificar novamente se ainda é ativa antes de fazer a chamada
      if (!this.isActiveInstance) {
        console.log(`[${this.componentId}] No longer active, aborting API call`);
        return;
      }
      
      console.log(`[${this.componentId}] About to call userValidation()`);
      
      this.subscription = this.authService.userValidation().subscribe({
        next: (response) => {
          // Verificar se ainda é ativa antes de processar resposta
          if (!this.isActiveInstance) {
            console.log(`[${this.componentId}] No longer active, ignoring success response`);
            return;
          }
          
          console.log(`[${this.componentId}] SUCCESS CALLBACK EXECUTED`);
          console.log(`[${this.componentId}] Response received:`, response);
          
          const measuredByName = response.data.name;
          const measuredByOrganisation = response.data.organization;
          const measurerEmail = response.data.email;
          const measurerTelephone = response.data.phone;

          const payeeName = response.data.clinicName;
          const clinicAddress1 = response.data.clinicAddress1;
          const clinicAddress2 = response.data.clinicAddress2;
          const clinicAddress3 = response.data.clinicAddress3;

          const clinicCity = response.data.clinicCity;
          const clinicState = response.data.clinicState;
          const clinicZipCode = response.data.clinicZipCode;

          const clinicEmail = response.data.clinicEmail;
          const clinicTelephone = response.data.clinicTelephoneNumber;

          this.storageService.setItem("Measured By - Name", measuredByName);
          this.storageService.setItem("Measured By - Organisation", measuredByOrganisation);
          this.storageService.setItem("Measurer Email", measurerEmail);
          this.storageService.setItem("Measurer Telephone", measurerTelephone);
          this.storageService.setItem("languageId", response.data.languageId);
          this.storageService.setItem("Is the shipping address different from the billing address?", 'No');

          this.storageService.setItem("Payee Name", payeeName ?? '');

          this.storageService.setItem("Payee Address 1", clinicAddress1 ?? '');
          this.storageService.setItem("Payee Address 2", clinicAddress2 ?? '');
          this.storageService.setItem("Payee Address 3", clinicAddress3 ?? '');

          this.storageService.setItem("Payee City", clinicCity ?? '');
          this.storageService.setItem("Payee State", clinicState ?? '');
          this.storageService.setItem("Payee Zip Code", clinicZipCode ?? '');

          this.storageService.setItem("Payee Email", clinicEmail ?? '');
          this.storageService.setItem("Payee Telephone Number", clinicTelephone ?? '');

          // Estado de sucesso
          this.currentState = 'success';
          this.cdr.detectChanges();
          console.log(`[${this.componentId}] SUCCESS: State changed to:`, this.currentState);
          
          setTimeout(() => {
            if (this.isActiveInstance) { // Verificar novamente antes de navegar
              this.router.navigate(['/en-us/order-creation']);
            }
          }, 2000); 
        },
        error: (error) => {
          // Verificar se ainda é ativa antes de processar erro
          if (!this.isActiveInstance) {
            console.log(`[${this.componentId}] No longer active, ignoring error response`);
            return;
          }
          
          console.log(`[${this.componentId}] ERROR CALLBACK EXECUTED`);
          console.log(`[${this.componentId}] Error received:`, error);
          
          // Estado de erro
          this.currentState = 'error';
          this.errorMessage = 'Session validation failed. Redirecting to login....';
          this.cdr.detectChanges();
          console.log(`[${this.componentId}] ERROR: State changed to:`, this.currentState);
          
          setTimeout(() => {
            if (this.isActiveInstance) { // Verificar novamente antes de navegar
              this.router.navigate(['/']);
            }
          }, 5000); 
        },
        complete: () => {
          console.log(`[${this.componentId}] COMPLETE CALLBACK EXECUTED`);
        }
      });
    }, 2000); 
  }
}